import { IKeyValueItem } from "../../../../../Global/Base/DataSources/Interfaces";
import { AttrStringValue } from "../../../../../Global/Base/DataTypes/AttrValue";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../../UI.BaseControls/Checkbox";
import { Control } from "../../../../UI.BaseControls/Control";
import { IValidatable } from "../../../../UI.BaseControls/ControlInterfaces";
import { EditableControl } from "../../../../UI.BaseControls/EditableControl";
import { NumEdit } from "../../../../UI.BaseControls/NumEdit";
import { TimeEdit } from "../../../../UI.BaseControls/TimeEdit";
import { IDropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { createElement, registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IHasFrameContext } from "../../Controls/ControlInterfaces";
import { FrameContext } from "../../UIManagerInterfaces";
import { BaseAdvancedFilters } from "./AdvancedFilters/BaseAdvancedFilters";
import { DateTimeWithInclude } from "./AdvancedFilters/DateTimeWithInclude";
import { ExtendedFilterEdit } from "./AdvancedFilters/ExtendedFilterEdit";
import { IKeyValueItemExtended } from "./AdvancedFilters/ValueList";
import { DropDownTextAreaItem } from "./DropDownMenuItems/DropDownTextAreaItem";
import { FilterCombo } from "./FilterCombo";
import {
    FilterJsonResultSign,
    FilterTargetType,
    IFilterBlock,
    IFilterGroup,
    IFilterItem,
    IFilterJsonResult,
    IFilterJsonResultValue,
    IFilterUserValue,
    IOperation,
    IReplaceFilterItem,
} from "./FilterInterfaces";
import { FilterRegistrations } from "./FilterRegistrations";
import { IStructureExtension } from "./IStructureExtension";
import { IWithInclude } from "./IWithInclude";

/**
 * Třída obsahující základní filter pro jednu filtrační operaci
 */
export class FilterItem extends Control implements IFilterItem, IHasFrameContext {
    /**
     * Nadřazený filtrační blok
     */
    public parent: IFilterBlock;
    public dataType: string;
    protected _group: IFilterGroup; // nadřazena skupina
    protected _isUsed: boolean;
    protected _isChanged: boolean;

    protected _curStruct: IOperation; // sablona operace pro dany FilterItem

    //#region Elementy pro přepnutí OR nebo AND podmínky pro předchozí FilterItem
    protected _conditionElement: HTMLElement; // nadrazena polozka pro [OR | AND]Btn Elementy
    protected _orBtnElement: HTMLElement; // Element pro podmínku OR
    protected _andBtnElement: HTMLElement; // Element pro podmínku AND
    //#endregion Elementy pro přepnutí OR nebo AND podmínky pro předchozí FilterItem

    protected _filterListeners: any;

    protected _topContent: HTMLElement; // element hlavičky obsahujici filterCombo, isUsedElement, removeElement
    protected _fieldContent: HTMLElement; // nadřazená položka pro filtrační pole vygenerovana na základě struktury
    protected _filterCombo: FilterCombo; // kombo obsahujici seznam podmínek pro připadnou změnu podmínky pro item
    protected _isUsedElement: Checkbox; // checkbox pro vypnutí/zapnutí použití filtru
    protected _removeElement: HTMLElement; // element pro odebrání
    protected _curSelectedOperation: FilterJsonResultSign; // Aktuální znaménko propojující predchozi FilterItem s tímto

    private _firstInputElement: EditableControl;
    private _lastUsedClass: string;
    private _parentMenu: IDropDownMenu;
    /** Kontext framu */
    public frameContext: FrameContext | undefined = undefined;

    /**
     * Aktuální znaménko propojující predchozi FilterItem s tímto
     */
    public get currentSign(): FilterJsonResultSign {
        return this._curSelectedOperation;
    }

    public set currentSign(v: FilterJsonResultSign) {
        this._curSelectedOperation = v;

        this._orBtnElement.classList.toggle("active", !(v === "and"));
        this._andBtnElement.classList.toggle("active", (v === "and"));
    }

    public get parentMenu(): IDropDownMenu {
        return this._parentMenu;
    }

    public set parentMenu(menu: IDropDownMenu) {
        this._parentMenu = menu;
        if (this._filterCombo)
            this._filterCombo.parentMenu = menu;
    }

    /**
     * Nadřazená skupina filtrů
     */
    public set group(value: IFilterGroup) {
        this._group = value;

        if (value.filterStructure.dataType === "flags") {
            Localization.addLocDescData(this._filterCombo, "GUI-690552", "title"); // Výběr barev
        } else if (!value.filterStructure.basicMultiOperation) { // U MultiOperation lze vybírat jen předdefinované hodnoty, takže ty popisky u ní nemají smysl
            Localization.addLocDescData(this._filterCombo, "GUI-668875", "title"); // Vybrat podmínku
            Localization.addLocDescData(this._fieldContent, "GUI-726763", "title"); // Zadáním :: spustíte rozšířený filtr.
        }

        this._filterCombo.parseFromStructure(value.filterStructure, this._curStruct);
    }

    public get group(): IFilterGroup {
        return this._group;
    }

    public get isUsed(): boolean {
        if (this._isUsed && this.parent.type == FilterTargetType.column)
            return this._isChanged;
        return (this._isUsed && this._isUsedElement.enabled);
    }

    public set isUsed(v: boolean) {
        this._isUsed = v;
        this._isUsedElement.checked = v;
    }

    public get operationId(): string {
        return this._curStruct ? this._curStruct.id : undefined;
    }

    /**
     * Obsahuje informace o tom, zda byla skupina nekdy zmenena
     */
    public get isChanged(): boolean {
        return this._isChanged;
    }

    /**
     * Načtení filtru ze struktury
     * @param struct Struktura pro danou operaci
     * @param values Hodnoty pro pole
     */
    public loadFromStructure(struct: IOperation, values?: { [id: string]: IFilterJsonResultValue }): void {
        this._curStruct = struct;
        if (this._lastUsedClass)
            this.classList.remove(this._lastUsedClass);
        this.classList.add(struct.id);
        this._lastUsedClass = struct.id;
        // Pokud se pomocí filterCombo přepne podmínka, muzou být některé hodnoty už vyplněné, takže
        // se před smazáním starých polí hodnoty uloží do dočasného pole na základě jejich ID a potom se
        // přesunou jako hodnoty nových, pokud není vyplněna položka values.
        const fieldOldVal: Map<string, string> = new Map<string, string>(); // map starych poli
        const fieldOldSubOperationID: Map<string, string> = new Map<string, string>();
        const fields = Array.from(this._fieldContent.children) as EditableControl[];
        for (const field of fields) {
            fieldOldVal.set(field.getAttribute("filter-id"), field instanceof IFilterUserValue ? field.userValue : field.value);
            if (field instanceof IStructureExtension) {
                if (field.subOperationID) {
                    fieldOldSubOperationID.set(field.getAttribute("filter-id"), field.subOperationID);
                }
            }
        }

        this._fieldContent.innerHTML = "";

        if (struct.fieldList.length === 0) {
            this._isChanged = true;
        }

        for (const field of struct.fieldList) {
            const f = FilterRegistrations.getFieldInstance(field.fieldType); // vrati instanci daného pole
            if (f instanceof IHasFrameContext)
                f.frameContext = this.frameContext;
            if (!this._firstInputElement)
                this._firstInputElement = f;
            f.setAttribute("filter-id", field.id);
            f.setAttribute("filter-type", field.fieldType);
            f.setAttribute("filter-item-id", this._curStruct.id);
            f.setAttribute("data-attrname", this.group.attrName);
            f.setAttribute("data-classname", this.parent.objectName);

            if (f instanceof ExtendedFilterEdit) {
                f.dataType = this.dataType;
            }

            if (field.additional && field.additional.including && f instanceof IWithInclude) {
                f.includeVisible = true;
            }

            if (field.additional && field.additional.defaultValue) {
                f.defaultValue = field.additional.defaultValue;
            }

            if (this.group.displayFormat) {
                if (f instanceof NumEdit)
                    f.innerElement.setAttribute("displayformat", this.group.displayFormat);
                if (f instanceof TimeEdit)
                    f.setAttribute("timeformat", this.group.displayFormat);
                if (f instanceof DateTimeWithInclude)
                    f.applyDisplayFormat(this.group.displayFormat);
            }

            // zde se kontroluji zda jsou nejake nove hodnoty predany nebo existuji nejake stare
            if (values != undefined && values[field.id] != undefined) {
                if (f instanceof IWithInclude && values[field.id].additional && values[field.id].additional.including)
                    f.include = true;
                if (f instanceof IFilterUserValue && values[field.id].userValue)
                    f.userValue = values[field.id].userValue;
                else
                    f.value = values[field.id].value;
                this._isChanged = true;
            } else if (fieldOldVal.get(field.id))
                f.value = fieldOldVal.get(field.id);

            if (f instanceof IStructureExtension)
                f.subOperationID = struct.subId;

            if (f instanceof BaseAdvancedFilters) {
                f.menu.addEventListener("open", () => {
                    if (this.parentMenu)
                        this.parentMenu.openedSubmenu = f.menu;
                });
            }

            // změna z uloženého na neuložený
            f.afterAttach((control) => {
                if (control instanceof BaseAdvancedFilters) {
                    control.registerEventListener("executeFilter", () => {
                        this._isChanged = true;
                        this.parent.unsave();
                        this.parent.execute();
                    });
                }

                control.registerEventListener("change", () => {
                    this._isChanged = true;
                    this.parent.unsave();
                });

                control.registerEventListener("input", () => {
                    this.callEvent("filterShowImplementationCheckbox", undefined, true);
                }, undefined, { once: true });

                control.registerEventListener("filterExecuteRequest", () => {
                    this.parent.executeRequest = true;
                });

                if (!(control instanceof ExtendedFilterEdit)) {
                    control.registerEventListener("keyup", (e: any) => { // Událost input elementu
                        if (e instanceof KeyboardEvent)
                            this._executeKeyListener(e, f);
                        else if (e instanceof CustomEvent)
                            this._executeKeyListener(e.detail.original, f);
                    });
                }
            });

            this._fieldContent.appendChild(f as HTMLElement);
        }

        this._filterCombo.setValue(struct.captionLocKey || struct.lockey);
    }

    /**
     * Při odpovídající kombinaci kláves spustí filtrování
     * @param event Událost stisku klávesy
     * @param control Komponenta, která vyvolala událost
     */
    protected _executeKeyListener(event: KeyboardEvent, control: EditableControl): void {
        if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.altKey && control.validate()) {
            this._isChanged = true;
            this.parent.execute();
        }

        if (this._isShortcutAllowed()) {
            const val = (event.target as HTMLInputElement)?.value;

            if (val.length >= 2) {
                const trimmedStr = val.trimLeft();

                if (trimmedStr.length === 2 && trimmedStr === "::") {
                    (event.target as HTMLInputElement).value = "";

                    this._filterCombo.callEvent("ItemSelected", {
                        operation: {
                            id: "extended",
                            subId: undefined,
                            lockey: "GUI-685188",
                            fieldList: [{ id: "input6", fieldType: "ExtendedFilterEdit" }],
                            order: 99,
                            isDefault: false,
                            isVisible: true,
                        },
                    });
                    (this.querySelector("tsc-dropdowntextareaitem textarea") as DropDownTextAreaItem)?.focus();
                }
            }
        }
    }

    public override focus(): void {
        if (this._firstInputElement)
            this._firstInputElement.focus();
    }

    /** Resetuje textovou hodnotu comba na aktuálně vybranou operaci */
    public resetCaption(): void {
        this._filterCombo.setValue(this._curStruct.captionLocKey || this._curStruct.lockey);
    }

    /**
     * Vrátí hodnoty položek
     * @param isForExport Vrácený objekt, nebude mít definované property, které jsou defaultně true. (např. enabled)
     */
    public getResult(isForExport: boolean = false, filterTargetType: FilterTargetType): IFilterJsonResult {
        let subOperationId = null;
        const arr: Array<IFilterJsonResultValue> = [];
        const fields = Array.from(this._fieldContent.children) as EditableControl[];
        let valid: boolean = true;

        for (const field of fields) {
            let currentSubOperationId = undefined;
            if (field instanceof IStructureExtension) {
                if (field.subOperationID) {
                    currentSubOperationId = subOperationId = field.subOperationID;
                }
            }
            if (field instanceof IValidatable) {
                if (!field.validate()) {
                    valid = false;
                }
            }

            if (field instanceof IReplaceFilterItem) {
                const newItems = field.recreateFilter(this._curStruct);

                if (newItems.length > 0) {
                    for (const item of newItems) {
                        const operation = FilterRegistrations.getOperationByOperationId(this.dataType, item.operation);
                        this.group.createFilterItem({ operation: operation, values: item.values, isUsed: true, operator: item.operator });
                        if (item.operator) this._curSelectedOperation = item.operator;
                    }

                    this.parent.executeRequest = true;
                    this.remove();
                    continue;
                }
            }

            //Vysledkem může být any. V případě hodnoty jsou/nejsou může být bud jedna hodnota typu IKeyValueItem nebo jejich pole.
            let resultArray: any[] | any = [];
            if (field.value && field.value instanceof Array) {
                for (const val of field.value) {
                    if (val instanceof IKeyValueItemExtended && val.shortenedValue)
                        resultArray.push(new AttrStringValue(val.value, val.shortenedValue));
                    else
                        resultArray.push(val instanceof IKeyValueItem ? val.value : val);
                }
            } else
                resultArray = field.value instanceof IKeyValueItem ? field.value.value : field.value;

            const valueItem: IFilterJsonResultValue = {
                value: isForExport && !resultArray ? "" : resultArray,
                additional: field instanceof IWithInclude ? { including: field.include } : undefined,
                userValue: field instanceof IFilterUserValue ? field.userValue : undefined,
                fieldId: isForExport ? undefined : field.getAttribute("filter-id"), //pro export uživateli nepotřebujeme
                fieldType: isForExport ? undefined : field.getAttribute("filter-type"), //pro export uživateli nepotřebujeme
                subOperationId: currentSubOperationId,
            };
            if (isForExport && valueItem.additional == undefined && valueItem.userValue == undefined && valueItem)
                arr.push(!resultArray ? "" : resultArray); //pro export do JSON přidá pouze hodnotu, pokud není uvedeno nic dalšího. JSON pak bude čitelnější a stručnější.
            else
                arr.push(valueItem);
        }
        //const isUsed = this.Parent.Type == FilterTargetType.Column ? this.isChanged : this.IsUsed;

        return {
            operation: this._curStruct.id,
            subOperation: subOperationId ? subOperationId : undefined,
            operator: isForExport && this._curSelectedOperation === "or" ? undefined : this._curSelectedOperation,
            values: arr,
            enabled: isForExport && this.isUsed
                ? undefined
                : (filterTargetType === FilterTargetType.column ? this.isUsed : this._isUsed && this._isUsedElement.enabled),
            isValid: isForExport && valid ? undefined : valid,
        };
    }

    protected override _constructor(): void {
        super._constructor();

        this.classList.add("FilterItem");
        this._filterListeners = [];
        this._isUsed = true;
        this._isChanged = false;
        this._conditionElement = createElement({ classes: "condition" });
        this._orBtnElement = createElement({ classes: ["btn-condition", "or", "active"] });
        this._andBtnElement = createElement({ classes: ["btn-condition", "and"] });
        this._curSelectedOperation = "or"; // nastavení výchozího znaménka

        const innerSpanOr = createElement({ type: "span" });
        Localization.addLocData(innerSpanOr, "GUI-452734"); // Nebo
        this._orBtnElement.appendChild(innerSpanOr);

        const innerSpanAnd = createElement({ type: "span" });
        Localization.addLocData(innerSpanAnd, "GUI-598905"); // A zároveň
        this._andBtnElement.appendChild(innerSpanAnd);

        this._conditionElement.appendChild(this._orBtnElement);
        this._conditionElement.appendChild(this._andBtnElement);

        this._topContent = createElement({ classes: "top" });

        this._filterCombo = new FilterCombo();
        this._filterCombo.classList.add("changeCondition", "flat", "btn", "is-dropdown");
        this._filterCombo.canUseFilter = false;
        this._filterCombo.canSelectItem = false;
        this._filterCombo.afterAttach(() => {
            this._filterCombo.registerEventListener("filterExecuteRequest", () => this.parent.executeRequest = true);
        });

        this._fieldContent = createElement({ classes: "fields" });

        this.appendChild(this._conditionElement);
        this.appendChild(this._topContent);
        this.appendChild(this._fieldContent);

        this._topContent.appendChild(this._filterCombo as HTMLElement);

        const btnContent = createElement({ classes: "btn_bar" });
        this._isUsedElement = new Checkbox();
        this._isUsedElement.classList.add("is_used", "is-dot");
        Localization.addLocDescData(this._isUsedElement, "GUI-668872", "title");//Deaktivovat podmínku
        this._removeElement = createElement({ classes: ["icon", "remove", "ui-cross-small"] });
        Localization.addLocDescData(this._removeElement, "GUI-649338", "title");//odebrat
        btnContent.appendChild(this._isUsedElement as HTMLElement);
        btnContent.appendChild(this._removeElement);

        this._topContent.appendChild(btnContent);

        this._isUsedElement.isNullable = false;
        this._isUsedElement.standardAction = true;
        this._isUsedElement.checked = true;
    }

    private _hasExtendedFilterParsedListener(): boolean {
        return this.getEventListeners("ExtendedFilterParsed").length > 0;
    }

    private _isShortcutAllowed(): boolean {
        const dataType = this.dataType.toLowerCase();
        return !(dataType === "date" || dataType === "datetime" || dataType === "time");
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();

        // Kombo pro zmenu typu podmínky
        this._filterCombo.registerEventListener("ItemSelected", (e: CustomEvent) => {
            const operation: IOperation = e.detail.operation;
            this.loadFromStructure(operation);
            this.parent.unsave();

            if (operation.id === "extended") {
                this.callEvent("ExtendedFilterCreated", {
                    filter: this,
                }, true);
            }
        });

        if (this.operationId === "extended" && !this._hasExtendedFilterParsedListener()) {
            this.callEvent("ExtendedFilterCreated", {
                filter: this,
            }, true);
        }

        this._orBtnElement.addEventListener("click", this._filterListeners["orButton"] = () => {
            if (this._orBtnElement.classList.contains("Disabled"))
                return;
            this._orBtnElement.classList.toggle("active", true);
            this._andBtnElement.classList.toggle("active", false);
            this._curSelectedOperation = "or";
            this.parent.unsave();
        });

        this._andBtnElement.addEventListener("click", this._filterListeners["andButton"] = () => {
            if (this._andBtnElement.classList.contains("Disabled"))
                return;
            this._orBtnElement.classList.toggle("active", false);
            this._andBtnElement.classList.toggle("active", true);
            this._curSelectedOperation = "and";
            this.parent.unsave();
        });

        this._isUsedElement.addEventListener("change", this._filterListeners["isused"] = () => {
            this._isUsed = this._isUsedElement.checked;
            this.classList.toggle("notUsed", !this._isUsed);
            if (this._isUsed)
                Localization.addLocDescData(this._isUsedElement, "GUI-668872", "title"); //Deaktivovat podmínku
            else
                Localization.addLocDescData(this._isUsedElement, "GUI-668896", "title"); //Aktivovat podmínku
            this.parent.unsave();
        });

        this._removeElement.addEventListener("click", this._filterListeners["removeElement"] = () => {
            if (this._removeElement.classList.contains("Disabled"))
                return;
            this.remove();
        });
    }

    public override remove(): void {
        if (this.group.items.length === 1)
            this.group.remove();
        super.remove();
        this.parent.unsave();
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback(); // Odstraní všechny Listenery na FilterItem (včetně ExtendedFilterParsed)

        this._orBtnElement.removeEventListener("click", this._filterListeners["orButton"]);
        this._andBtnElement.removeEventListener("click", this._filterListeners["andButton"]);
        this._isUsedElement.removeEventListener("change", this._filterListeners["isused"]);
        this._removeElement.removeEventListener("click", this._filterListeners["removeElement"]);
    }
}

registerCustomElement("tsc-FilterItem", FilterItem);
